/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import * as constants from '../../common/constants';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { fetchTaxPayeeInfo, ImpoundPolicyListResponse, TaxPayeeResponse } from '../../common/queryFunctions';
import { epochToDate, formatDate, phoneNumberFormatter } from '../../common/UtilityFunctions';
import { useAuthContext } from '../../contexts/AuthContext';
import css from '../../css/loan-escrow-accruals-styles/TaxAccruals.module.css';
import useTaxPayeeInfo from '../../common/hooks/useTaxPayeeInfo';

interface ParcelTaxImpound extends ImpoundPolicyListResponse {
  taxAmountDue: number;
  parcelNo: string;
  taxAmountPaid: number;
  assessorCode: string;
  supTaxAmountDue: number;
  parcelStatus: string;
  supTaxAmountPaid: number;
  parcelArrComment: string;
}
interface ComponentProps {
  taxImpounds: Array<ImpoundPolicyListResponse>;
  assessorCodes: Set<string>;
}
const TaxAccruals: React.FC<ComponentProps> = (props) => {
  const { token } = useAuthContext();

  const [parcelTaxImpounds, setParcelTaxImpounds] = useState<{ [key: string]: ParcelTaxImpound[] }>({});
  const [showPaidImpoundLines, setShowPaidImpoundLines] = useState<boolean>(false);
  const [halfDecades, setHalfDecades] = useState<Array<number>>([]);
  const [selectedHalfDecade, setSelectedHalfDecade] = useState<string>('');

  const {
    data: taxPayeeInfo,
    status: taxPayeeStatus,
    error: taxPayeeErrors,
  } = useTaxPayeeInfo(props.assessorCodes, props.taxImpounds, token);

  const flattenedTaxImpounds: Array<ImpoundPolicyListResponse> = useMemo(() => {
    return props.taxImpounds.flatMap((impList) => impList);
  }, [props.taxImpounds]);

  //this effect removes the internal parcel array and applies the attributes to the impound array, this allows for more clarity when displaying impound payments on the UI
  useEffect(() => {
    const tempParcelTaxImpounds: Array<ParcelTaxImpound> = [];
    flattenedTaxImpounds.forEach((impound) => {
      if (impound.parcels.length > 0) {
        impound.parcels.forEach((parcel) => {
          tempParcelTaxImpounds.push({
            ...impound,
            taxAmountDue: parcel.amountDue,
            parcelNo: parcel.parcelNo,
            taxAmountPaid: parcel.amountPaid,
            assessorCode: parcel.assessorCode,
            supTaxAmountDue: parcel.supAmountDue,
            parcelStatus: parcel.parcelStatus,
            supTaxAmountPaid: parcel.supAmountPaid,
            parcelArrComment: parcel.parcelArrComment,
          });
        });
      }
      tempParcelTaxImpounds.forEach((impound) => {
        delete impound.parcels;
      });
    });
    tempParcelTaxImpounds.sort((a, b) => {
      //if impound types are the same, order by payment due date
      return a.renewalDte - b.renewalDte;
    }); //order by payment date
    //this bit of logic sorts the tax impounds by half-decade and payment status
    const indexes: { [key: string]: ParcelTaxImpound[] } = { ['unpaid']: [] };
    let currentHalfDecade: number = 0;
    let currentHalfDecadeFirstIndex: number = 0;
    for (let i = 0; i < tempParcelTaxImpounds.length; i++) {
      const currentParcelTaxImpound: ParcelTaxImpound = tempParcelTaxImpounds[i];
      const year: number = epochToDate(currentParcelTaxImpound.renewalDte).getFullYear();
      const halfDecade: number = Math.floor(year / 5) * 5;
      if (currentHalfDecade === 0) {
        currentHalfDecade = halfDecade;
      }
      if (halfDecade !== currentHalfDecade) {
        indexes[currentHalfDecade.toString()] = tempParcelTaxImpounds.slice(currentHalfDecadeFirstIndex, i);
        if (!halfDecades.includes(currentHalfDecade)) {
          setHalfDecades([...halfDecades, currentHalfDecade]);
        }
        currentHalfDecade = halfDecade;
        currentHalfDecadeFirstIndex = i;
      }
      if (
        currentParcelTaxImpound.taxAmountDue > currentParcelTaxImpound.taxAmountPaid ||
        currentParcelTaxImpound.supTaxAmountDue > currentParcelTaxImpound.supTaxAmountPaid
      ) {
        indexes['unpaid'] = [...indexes['unpaid'], currentParcelTaxImpound];
      }
    }
    if (tempParcelTaxImpounds.length !== 0) {
      indexes[currentHalfDecade.toString()] = tempParcelTaxImpounds.slice(currentHalfDecadeFirstIndex);
      if (!halfDecades.includes(currentHalfDecade)) {
        setHalfDecades([...halfDecades, currentHalfDecade]);
      }
    }
    setParcelTaxImpounds(indexes);
  }, [flattenedTaxImpounds, halfDecades]);

  //sets the selected half decade to the first half-decade upon population of the half-decade array
  useEffect(() => {
    if (halfDecades.length > 0) {
      halfDecades.sort((a, b) => a - b);
      setSelectedHalfDecade(halfDecades[0].toString());
    }
  }, [halfDecades, showPaidImpoundLines]);

  const handlePaidImpoundToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    setShowPaidImpoundLines(value);
  }, []);

  const handleHalfDecadeSelection = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value: string = event.target.value;
    setSelectedHalfDecade(value);
  }, []);

  if (taxPayeeStatus === 'pending') return <div>Loading...</div>;
  if (taxPayeeErrors.length > 0)
    return (
      <>
        <h2>Encountered errors while obtaining Tax Information.</h2>
        <ul>
          {taxPayeeErrors.map((queryError, index) => {
            return <li key={index}>{queryError.message}</li>;
          })}
        </ul>
      </>
    );

  return (
    <>
      <form>
        <span>Show Fully Paid Impound Lines?</span>
        <input
          type='checkbox'
          name='showPaidImpoundLines'
          checked={showPaidImpoundLines}
          onChange={handlePaidImpoundToggle}
        ></input>
        {showPaidImpoundLines && halfDecades.length > 0 && (
          <div>
            <span>Select Half-Decade:</span>
            {halfDecades.map((halfDecade, index) => (
              <label key={index} className={css.radioLabel}>
                <input
                  type='radio'
                  value={halfDecade.toString()}
                  checked={selectedHalfDecade === halfDecade.toString()}
                  onChange={handleHalfDecadeSelection}
                ></input>
                {`${halfDecade.toString()} - ${(halfDecade + 4).toString()}`}
              </label>
            ))}
          </div>
        )}
      </form>
      <table className={css.taxTable}>
        <caption className='h3 semibold'>Tax&nbsp;Accruals</caption>
        <thead>
          <tr>
            <th>Impound Type</th>
            <th>Payee</th>
            <th>Due Date</th>
            <th>Payment Due</th>
            <th>Sup. Payment Due</th>
            <th>Payment Status</th>
            <th>Tax No(s).</th>
            <th>Status</th>
            <th>Comment</th>
          </tr>
        </thead>
        <tbody>
          {Object.values(parcelTaxImpounds).some((impounds) => Array.isArray(impounds) && impounds.length > 0) &&
            parcelTaxImpounds[!showPaidImpoundLines ? 'unpaid' : selectedHalfDecade].map((impound, index) => {
              const payeeInfo: TaxPayeeResponse = taxPayeeInfo.find(
                (payee) => payee.assessorCode === impound.assessorCode,
              );
              const parcelNo: string = impound.parcelNo;
              const paymentStatus: string =
                impound.parcelStatus !== constants.impPolicyStatusEscrowed
                  ? 'N/A'
                  : impound.taxAmountDue <= impound.taxAmountPaid && impound.supTaxAmountDue <= impound.supTaxAmountPaid
                    ? 'FULLY PAID'
                    : impound.taxAmountPaid > 0 || impound.supTaxAmountPaid > 0
                      ? 'PARTLY PAID'
                      : 'NOT PAID';
              return (
                <tr key={index}>
                  <td>{impound.impoundType}</td>
                  {payeeInfo ? (
                    <td>{`${payeeInfo.nameOne} ${phoneNumberFormatter(payeeInfo.phone, payeeInfo.phoneExt)}`}</td>
                  ) : (
                    <td></td>
                  )}
                  {impound.renewalDte ? <td>{formatDate(epochToDate(impound.renewalDte))}</td> : <td></td>}
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={impound.taxAmountDue}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={impound.supTaxAmountDue}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                  <td>{paymentStatus}</td>
                  <td>{parcelNo}</td>
                  {impound.parcelStatus === constants.impPolicyStatusEscrowed ? (
                    <td>Escrowed</td>
                  ) : (
                    <td>Not Escrowed</td>
                  )}
                  <td>{impound.parcelArrComment}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </>
  );
};

export default TaxAccruals;
